import {
  AutonomousActivityType,
  ScvAutonomyAuxControlAction,
} from "protos/atk/enums";
import {
  ScvAutonomyActivitiesConfiguration,
  ScvAutonomyHeightControlConfiguration,
  ThreePointAutonomyControlConfiguration,
} from "protos/atk/autotractor";

type ScvAutonomyHeightActivity = "none" | "scv-off" | "hold-height";

type ThreePointAutonomyHoldActivity = "hold-height" | "hold-float" | "none";
type ThreePointAutonomyEntryActivity =
  | "go-to-height"
  | "detent-up"
  | "detent-down"
  | "none";

interface ThreePointControlViewModel {
  entryActivity: ThreePointAutonomyEntryActivity;
  entryGoToHeightPct: number;
  holdActivity: ThreePointAutonomyHoldActivity;
  holdHeightPct: number;
}

export interface ImplementActivitiesFormViewModel {
  activities: {
    [activity in AutonomousActivityType]?: {
      heightActivity: ScvAutonomyHeightActivity;
      heightPct: number;
      aux1Action: ScvAutonomyAuxControlAction;
      aux2Action: ScvAutonomyAuxControlAction;
      frontThreePtControl: ThreePointControlViewModel;
      rearThreePtControl: ThreePointControlViewModel;
      minTransitionVelocityMps: number;
      maxTransitionWaitS: number;
    };
  };
}

function threePointControlConfigToViewModel(
  config: ThreePointAutonomyControlConfiguration | undefined
): ThreePointControlViewModel {
  let entryActivity: ThreePointAutonomyEntryActivity = "none";
  let entryGoToHeightPct = 50;
  let holdActivity: ThreePointAutonomyHoldActivity = "none";
  let holdHeightPct = 50;

  if (config?.entryGoToTargetHeight !== undefined) {
    entryActivity = "go-to-height";
    // height in the underlying config object is specified as a fraction, not
    // a percentage; convert for the view model
    entryGoToHeightPct = config.entryGoToTargetHeight * 100;
  } else if (config?.entryDetentUp) {
    entryActivity = "detent-up";
  } else if (config?.entryDetentDown) {
    entryActivity = "detent-down";
  }

  if (config?.holdTargetHeight !== undefined) {
    holdActivity = "hold-height";
    // height in the underlying config object is specified as a fraction, not
    // a percentage; convert for the view model
    holdHeightPct = config.holdTargetHeight * 100;
  } else if (config?.holdFloat) {
    holdActivity = "hold-float";
  }

  return {
    entryActivity,
    entryGoToHeightPct,
    holdActivity,
    holdHeightPct,
  };
}

function viewModelToThreePointControlConfig(
  viewModel: ThreePointControlViewModel
): ThreePointAutonomyControlConfiguration {
  const config: ThreePointAutonomyControlConfiguration = {};

  switch (viewModel.entryActivity) {
    case "go-to-height": {
      // height in the underlying config object is stored as a fraction, not a
      // percentage; convert back
      config.entryGoToTargetHeight = viewModel.entryGoToHeightPct / 100;
      break;
    }
    case "detent-up": {
      config.entryDetentUp = {};
      break;
    }
    case "detent-down": {
      config.entryDetentDown = {};
      break;
    }
    case "none": {
      config.entryNone = {};
      break;
    }
  }

  switch (viewModel.holdActivity) {
    case "hold-height": {
      // height in the underlying config object is stored as a fraction, not a
      // percentage; convert back
      config.holdTargetHeight = viewModel.holdHeightPct / 100;
      break;
    }
    case "hold-float": {
      config.holdFloat = {};
      break;
    }
    case "none": {
      config.holdNone = {};
      break;
    }
  }

  return config;
}

export function activitiesConfigToViewModel(
  config: ScvAutonomyActivitiesConfiguration | undefined
): ImplementActivitiesFormViewModel {
  const DEFAULT_3PT_CONTROL = threePointControlConfigToViewModel(undefined);
  const DEFAULT_STATE: ImplementActivitiesFormViewModel["activities"][1] = {
    heightActivity: "none",
    heightPct: 50,
    aux1Action: ScvAutonomyAuxControlAction.AUX_CONTROL_ACTION_NONE,
    aux2Action: ScvAutonomyAuxControlAction.AUX_CONTROL_ACTION_NONE,
    frontThreePtControl: DEFAULT_3PT_CONTROL,
    rearThreePtControl: DEFAULT_3PT_CONTROL,
    minTransitionVelocityMps: 0.1,
    maxTransitionWaitS: 5,
  };

  const viewModel: ImplementActivitiesFormViewModel = {
    activities: {
      [AutonomousActivityType.ACTIVITY_WORKING_ROW]: DEFAULT_STATE,
      [AutonomousActivityType.ACTIVITY_WORKING_HEADLAND]: DEFAULT_STATE,
      [AutonomousActivityType.ACTIVITY_WORKING_DIVERT]: DEFAULT_STATE,
      [AutonomousActivityType.ACTIVITY_TRAVERSE]: DEFAULT_STATE,
    },
  };

  for (const activity of config?.activities ?? []) {
    let heightActivity: ScvAutonomyHeightActivity = "none";
    let heightPct = 50;

    if (activity.heightControl?.holdTargetHeight !== undefined) {
      heightActivity = "hold-height";
      // height in the underlying config object is specified as a fraction, not
      // a percentage; convert for the view model
      heightPct = activity.heightControl.holdTargetHeight * 100;
    } else if (activity.heightControl?.scvOff) {
      heightActivity = "scv-off";
    }

    viewModel.activities[activity.activityType] = {
      heightActivity,
      heightPct,
      aux1Action:
        activity.aux1Control?.entryAction ??
        ScvAutonomyAuxControlAction.AUX_CONTROL_ACTION_NONE,
      aux2Action:
        activity.aux2Control?.entryAction ??
        ScvAutonomyAuxControlAction.AUX_CONTROL_ACTION_NONE,
      frontThreePtControl: threePointControlConfigToViewModel(
        activity.frontThreePtControl
      ),
      rearThreePtControl: threePointControlConfigToViewModel(
        activity.rearThreePtControl
      ),
      minTransitionVelocityMps: activity.minTransitionVelocityMps,
      maxTransitionWaitS: activity.maxTransitionWaitS,
    };
  }

  return viewModel;
}

export function viewModelToActivitiesConfig(
  viewModel: ImplementActivitiesFormViewModel
): ScvAutonomyActivitiesConfiguration {
  const config: ScvAutonomyActivitiesConfiguration = { activities: [] };

  for (const [activityStr, activityState] of Object.entries(
    viewModel.activities
  )) {
    // Object#entries turns the number keys into strings
    const activity = Number.parseInt(activityStr) as AutonomousActivityType;
    let heightControl: ScvAutonomyHeightControlConfiguration;

    if (activityState.heightActivity === "none") {
      heightControl = { none: {} };
    } else if (activityState.heightActivity === "scv-off") {
      heightControl = { scvOff: {} };
    } else {
      // height in the underlying config object is stored as a fraction, not a
      // percentage; convert back
      heightControl = { holdTargetHeight: activityState.heightPct / 100 };
    }

    config.activities.push({
      activityType: activity,
      heightControl,
      aux1Control: { entryAction: activityState.aux1Action },
      aux2Control: { entryAction: activityState.aux2Action },
      frontThreePtControl: viewModelToThreePointControlConfig(
        activityState.frontThreePtControl
      ),
      rearThreePtControl: viewModelToThreePointControlConfig(
        activityState.rearThreePtControl
      ),
      minTransitionVelocityMps: activityState.minTransitionVelocityMps,
      maxTransitionWaitS: activityState.maxTransitionWaitS,
    });
  }

  return config;
}
